/**
 * `<recently-viewed>` custom element (roadmap v1.1).
 *
 * Storage: `thcabina:recently-viewed` — a JSON array of product handles,
 * most recent first, capped at 12 (registered `thcabina:` key).
 *
 * On a PDP (data-product-handle set) it records the visit, then re-requests
 * its own section on /search with `q=handle:a OR handle:b&type=product` and
 * swaps in the returned markup, reordered to match the visit order. Stays
 * hidden without history, without JS, or when localStorage is unavailable.
 */
class RecentlyViewed extends HTMLElement {
  static STORAGE_KEY = 'thcabina:recently-viewed';
  static MAX = 12;

  connectedCallback() {
    const current = this.dataset.productHandle;
    const handles = this.read();

    if (current) {
      const updated = [current].concat(handles.filter((h) => h !== current)).slice(0, RecentlyViewed.MAX);
      this.write(updated);
    }

    const toShow = handles.filter((h) => h !== current);
    if (toShow.length === 0) return;

    const query = toShow.map((h) => `handle:${h}`).join(' OR ');
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    fetch(`${root}search?section_id=${this.dataset.sectionId}&type=product&q=${encodeURIComponent(query)}`)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const updated = doc.querySelector('recently-viewed');
        if (!updated || !updated.querySelector('.product-card')) return;
        this.innerHTML = updated.innerHTML;
        this.reorder(toShow);
        this.hidden = false;
      })
      .catch(() => {});
  }

  /* La search non preserva l'ordine di visita: le card tornano nell'ordine
     dell'indice e vanno rimesse in ordine cronologico inverso. */
  reorder(handles) {
    const grid = this.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;
    const cards = Array.from(grid.children);
    const position = (card) => {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return handles.length;
      const handle = link.getAttribute('href').split('/products/')[1].split(/[?#]/)[0];
      const index = handles.indexOf(handle);
      return index === -1 ? handles.length : index;
    };
    cards
      .sort((a, b) => position(a) - position(b))
      .forEach((card) => grid.appendChild(card));
  }

  read() {
    try {
      return JSON.parse(window.localStorage.getItem(RecentlyViewed.STORAGE_KEY)) || [];
    } catch (error) {
      return [];
    }
  }

  write(handles) {
    try {
      window.localStorage.setItem(RecentlyViewed.STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      /* storage pieno o negato: la cronologia semplicemente non si aggiorna */
    }
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}
